import React, { useState } from 'react';
import { useSeller } from '../../context/SellerContext';
import InputField from '../InputField';
import FAQSection from '../FAQSection';
import {
  validatePincode,
  validateRequired,
  validateCity,
  validateState,
} from '../../utils/validation';
import { pincodeAPI, handleAPIError } from '../../utils/sellerAPI';

const indianStates = [
  'Andhra Pradesh',
  'Arunachal Pradesh',
  'Assam',
  'Bihar',
  'Chhattisgarh',
  'Goa',
  'Gujarat',
  'Haryana',
  'Himachal Pradesh',
  'Jharkhand',
  'Karnataka',
  'Kerala',
  'Madhya Pradesh',
  'Maharashtra',
  'Manipur',
  'Meghalaya',
  'Mizoram',
  'Nagaland',
  'Odisha',
  'Punjab',
  'Rajasthan',
  'Sikkim',
  'Tamil Nadu',
  'Telangana',
  'Tripura',
  'Uttar Pradesh',
  'Uttarakhand',
  'West Bengal',
];

const PickupAddressForm = ({ onNext, onPrev }) => {
  const {
    formData,
    updateNestedFormData,
    errors,
    setError,
    clearAllErrors,
    loading,
    setLoading,
  } = useSeller();

  const [pincodeLookupLoading, setPincodeLookupLoading] = useState(false);

  const handleAddressChange = (field, value) => {
    updateNestedFormData('pickupAddress', field, value);
    clearAllErrors(field);
  };

  const handlePincodeChange = async (value) => {
    updateNestedFormData('pickupAddress', 'pincode', value);
    clearAllErrors('pickupAddress.pincode');

    // Auto-fill city/state when 6 digits entered
    if (value.length === 6 && /^\d{6}$/.test(value)) {
      setPincodeLookupLoading(true);
      try {
        const response = await pincodeAPI.lookup(value);
        if (response.data.success) {
          updateNestedFormData('pickupAddress', 'city', response.data.city);
          updateNestedFormData('pickupAddress', 'state', response.data.state);
          updateNestedFormData('pickupAddress', 'district', response.data.district || '');
          clearAllErrors('city');
          clearAllErrors('state');
        }
      } catch (err) {
        const apiError = handleAPIError(err);
        if (apiError.status === 404) {
          setError('pincode', 'Pincode not found. Please enter manually.');
        }
      } finally {
        setPincodeLookupLoading(false);
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const validationErrors = {};

    if (!formData.pickupAddress.useEnrolmentAddress) {
      const buildingError = validateRequired(
        formData.pickupAddress.buildingNumber,
        'Building number'
      );
      const streetError = validateRequired(
        formData.pickupAddress.street,
        'Street'
      );
      const pincodeError = validatePincode(formData.pickupAddress.pincode);
      const stateError = validateState(formData.pickupAddress.state);
      const cityError = validateCity(formData.pickupAddress.city);

      if (buildingError) validationErrors.buildingNumber = buildingError;
      if (streetError) validationErrors.street = streetError;
      if (pincodeError) validationErrors.pincode = pincodeError;
      if (stateError) validationErrors.state = stateError;
      if (cityError) validationErrors.city = cityError;
    }

    if (Object.keys(validationErrors).length > 0) {
      Object.entries(validationErrors).forEach(([field, error]) => {
        setError(field, error);
      });
      return;
    }

    onNext();
  };

  const pickupFAQs = [
    {
      question: 'Who will pick up the products for delivery?',
      answer:
        'All products will be picked up by Kalinga certified delivery partners from your location and delivered directly to the customer.',
    },
    {
      question: 'Do I need to pay the shipping charges?',
      answer: 'No, shipping is completely free to 24,000+ pincodes in India.',
    },
    {
      question: 'How can I change shipping state',
      answer:
        'You can update your pickup address anytime from your seller dashboard.',
    },
  ];

  return (
    <div className="w-full">
      <div className="max-w-2xl mx-auto">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Use Enrolment Address Option */}
          <div className="bg-white rounded-lg p-6 border border-gray-200">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.pickupAddress.useEnrolmentAddress}
                onChange={(e) =>
                  handleAddressChange('useEnrolmentAddress', e.target.checked)
                }
                className="w-4 h-4 text-blue-600"
              />
              <span className="ml-3 font-medium text-gray-700">
                Use address registered on Enrolment ID / UIN
              </span>
            </label>
          </div>

          {/* Address Form */}
          {!formData.pickupAddress.useEnrolmentAddress && (
            <div className="bg-white rounded-lg p-6 border border-gray-200 space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-yellow-800">
                  📌 Products will be picked up from this location for delivery
                </p>
              </div>

              <InputField
                label="Room/Floor/Building Number"
                type="text"
                name="buildingNumber"
                value={formData.pickupAddress.buildingNumber}
                onChange={(e) => handleAddressChange('buildingNumber', e.target.value)}
                placeholder="e.g., 1208a, Floor 3, etc."
                error={errors.buildingNumber}
                required
              />

              <InputField
                label="Street/Locality"
                type="text"
                name="street"
                value={formData.pickupAddress.street}
                onChange={(e) => handleAddressChange('street', e.target.value)}
                placeholder="e.g., K Block, Main Road"
                error={errors.street}
                required
              />

              <InputField
                label="Landmark"
                type="text"
                name="landmark"
                value={formData.pickupAddress.landmark}
                onChange={(e) => handleAddressChange('landmark', e.target.value)}
                placeholder="e.g., Near City Center"
                error={errors.landmark}
              />

              <div className="grid grid-cols-2 gap-4">
                <InputField
                  label="Pincode"
                  type="text"
                  name="pincode"
                  value={formData.pickupAddress.pincode}
                  onChange={(e) => handlePincodeChange(e.target.value.slice(0, 6))}
                  placeholder="201009"
                  error={errors.pincode}
                  maxLength="6"
                  required
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    City <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.pickupAddress.city}
                    onChange={(e) => handleAddressChange('city', e.target.value)}
                    placeholder="e.g., Ghaziabad"
                    className={`
                      w-full px-4 py-2 border border-gray-300 rounded-lg
                      focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                      ${errors.city ? 'border-red-500 bg-red-50' : ''}
                    `}
                  />
                  {errors.city && (
                    <p className="text-red-500 text-sm mt-1">{errors.city}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  State <span className="text-red-500">*</span>
                </label>
                <select
                  name="state"
                  value={formData.pickupAddress.state}
                  onChange={(e) => handleAddressChange('state', e.target.value)}
                  className={`
                    w-full px-4 py-2 border border-gray-300 rounded-lg
                    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                    ${errors.state ? 'border-red-500 bg-red-50' : ''}
                  `}
                >
                  <option value="">Select State</option>
                  {indianStates.map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
                </select>
                {errors.state && (
                  <p className="text-red-500 text-sm mt-1">{errors.state}</p>
                )}
              </div>

              <InputField
                label="District"
                type="text"
                name="district"
                value={formData.pickupAddress.district || ''}
                onChange={(e) => handleAddressChange('district', e.target.value)}
                placeholder="Enter district"
              />
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-4">
            <button
              type="button"
              onClick={onPrev}
              className="flex-1 py-2 px-4 border border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 transition"
            >
              Back
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`
                flex-1 py-2 px-4 rounded-lg font-semibold text-white
                transition duration-200
                ${loading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
                }
              `}
            >
              {loading ? 'Processing...' : 'Continue'}
            </button>
          </div>
        </form>
      </div>

      <div className="mt-8">
        <FAQSection faqs={pickupFAQs} />
      </div>
    </div>
  );
};

export default PickupAddressForm;
